/**
 * The sidebar's order — the arithmetic, and where it is remembered.
 *
 * ## A stored order is never a permutation of what is on screen
 *
 * That is the whole design constraint. The order is a list of ids written
 * down at some point in the past; by the time it is read back a saved view
 * may have been removed, a project deleted, a workspace switched. So
 * `applyOrder` treats the stored list as a PREFERENCE over the real items
 * rather than as the list itself: stored ids that still exist come first, in
 * the order given; everything else follows in its natural order; a stored id
 * that has gone is skipped, and a repeated one is used once. Every item
 * appears exactly once no matter what storage holds — the invariant
 * `reorder.test.ts` asserts directly, because "the sidebar lost a project"
 * is a defect nobody notices until weeks after the rename that caused it.
 *
 * ## Why storage is injected
 *
 * `localStorage` is the right store for this — it is a per-viewer display
 * preference, not shared state, and it must survive a reload — but it has
 * two failure modes that are easy to write code that ignores. A private
 * window or a browser set to block site data throws from the ACCESSOR
 * itself, not merely from `getItem`; and an environment without it (a server
 * render, a test) has no `localStorage` at all. Both end in "no stored
 * order", never in an exception, and both are reachable from a test because
 * the store is a parameter.
 */

/** The two methods this module needs. `localStorage` satisfies it. */
export interface OrderStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Where the console writes its sidebar orders. Namespaced so a key cannot
 *  collide with anything else the origin stores. */
export const ORDER_KEY = {
  nav: "ledai.console.order.nav",
  views: "ledai.console.order.views",
  projects: "ledai.console.order.projects",
} as const;

/**
 * The browser's own store, or `null` where there is none.
 *
 * Reading the PROPERTY is what throws in a browser configured to block site
 * data, so the access itself is inside the `try` — a check like
 * `typeof localStorage !== "undefined"` would throw before it could answer.
 */
export function browserStorage(): OrderStorage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * `items`, arranged by `stored`. See the file header for the rule; the short
 * version is that every item comes out exactly once whatever `stored` says.
 */
export function applyOrder<T>(
  items: readonly T[],
  idOf: (item: T) => string,
  stored: readonly string[],
): T[] {
  const remaining = new Map<string, T>();
  for (const item of items) {
    const id = idOf(item);
    // First wins, so two items sharing an id cannot make one disappear.
    if (!remaining.has(id)) remaining.set(id, item);
  }

  const ordered: T[] = [];
  for (const id of stored) {
    const item = remaining.get(id);
    // Absent: either the id has gone, or it was already placed by an earlier
    // duplicate. Both are skips.
    if (item === undefined) continue;
    remaining.delete(id);
    ordered.push(item);
  }
  // Everything storage did not name keeps its natural order, at the end —
  // which is where a newly shipped view or a newly created project belongs.
  return [...ordered, ...remaining.values()];
}

/**
 * `ids` with one id moved: by a step (`-1` up, `+1` down) for the keyboard,
 * or ONTO another id for a drop.
 *
 * A step past either end is a no-op rather than a wrap. A row that leapt from
 * the top of the list to the bottom because the user pressed the key once
 * more is indistinguishable from a bug.
 */
export function moveWithin(ids: readonly string[], id: string, to: number | string): string[] {
  const from = ids.indexOf(id);
  if (from === -1) return [...ids];

  const target = typeof to === "number" ? from + to : ids.indexOf(to);
  if (target === -1 || target === from || target < 0 || target >= ids.length) return [...ids];

  const next = [...ids];
  next.splice(from, 1);
  next.splice(target, 0, id);
  return next;
}

/** The stored order under `key`, or `[]` — for missing, unreadable, or
 *  malformed content alike. This function does not throw. */
export function readOrder(
  key: string,
  storage: OrderStorage | null | undefined = browserStorage(),
): string[] {
  if (!storage) return [];
  try {
    const raw = storage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // All-or-nothing: a list with a non-string in it was not written by this
    // module, and half-trusting it would put an unrenderable value in a key.
    if (!parsed.every((value) => typeof value === "string")) return [];
    return parsed as string[];
  } catch {
    return [];
  }
}

/** Remember `ids` under `key`. A store that refuses is not an error — the
 *  order simply does not survive the reload. */
export function writeOrder(
  key: string,
  ids: readonly string[],
  storage: OrderStorage | null | undefined = browserStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(ids));
  } catch {
    // A private window, or a full quota. Nothing to report and nothing to do:
    // the sidebar is correct either way, it just forgets.
  }
}
